import { BaseElement, defineElement } from '@webtides/element-js';
import RetailerPlacesInputEvents from './RetailerPlacesInputEvents';
import ModalElementEvent from '../../../elements/_modal/modal-element/ModalElementEvent';
import loadScript from '../../../../js/util/load-script';
import UserCentrics from '../../../../js/util/UserCentrics';

export default class RetailerPlacesInput extends BaseElement {
    #modal = null;
    #googleMode = false;

    properties() {
        return {
            searchString: '',
            googleMapsApiKey: 'GOOGLE_MAPS_API_KEY',
            countries: ['de'],
        };
    }

    watch() {
        return {
            searchString: () => {
                this.dispatch(
                    RetailerPlacesInputEvents.PLACES_INPUT_CHANGE,
                    {
                        searchString: this.searchString,
                    },
                    true
                );
            },
        };
    }

    events() {
        return {
            window: {
                UC_UI_INITIALIZED: event => {
                    if (UserCentrics.hasGoogleMapsConsent()) {
                        this.#modal = this.closest('modal-element');
                        this.#modal?.addEventListener(ModalElementEvent.OPEN, () => {
                            this.loadGoogle();
                        });
                        if (this.#modal.openState) {
                            this.loadGoogle();
                        }
                    }
                },
            },
            '[ref="input"]': {
                keydown: ({ keyCode, target: input }) => {
                    if (this.#googleMode === false && keyCode === 13) {
                        this.searchString = input.value;
                    }
                },
            },
            '[ref="button"]': {
                click: event => {
                    // Set the search string to fire a PLACES_INPUT_CHANGE
                    // event. The {retailer-search} will listen to that event
                    // and update the internal {locationSearchString} which will
                    // in turn fire a new search request.
                    this.searchString = this.$refs.input.value;
                },
            },
        };
    }

    loadGoogle() {
        if (
            typeof window.google !== 'undefined' &&
            window.google.maps.LatLngBounds &&
            window.google.maps.places &&
            window.google.maps.places.Autocomplete
        ) {
            //google maps sdk seems to be loaded
            this.initGoogle(true);
        } else {
            // define google callback init
            window.initRetailerSearch = () => {
                this.initGoogle(true);
            };
            const googleMapsUrl = `https://maps.googleapis.com/maps/api/js?key=${this.googleMapsApiKey}&libraries=places&language=de&callback=initRetailerSearch`;
            loadScript(googleMapsUrl);
        }
    }

    initGoogle(focus = false) {
        if (this.#googleMode) {
            //already initialized
            return;
        }

        this.searchString = '';
        this.#googleMode = true;
        this.placeBounds = new google.maps.LatLngBounds();

        if (focus) {
            this.$refs.input?.focus();
        }
        // Create the search box and link it to the UI element.
        this.searchBox = new google.maps.places.Autocomplete(this.$refs.input, {
            componentRestrictions: { country: this.countries },
            types: ['(regions)'],
        });

        this.searchBox.addListener('place_changed', this.onPlaceChange.bind(this));
        // Enable selecting the first autocomplete prediction item by hitting enter
        google.maps.event.addDomListener(this.$refs.input, 'keydown', e => {
            e.cancelBubble = true; // Maps API e.stopPropagation();

            // if enter key, or tab key
            if (e.keyCode === 13 || e.keyCode === 9) {
                // if user isn't navigating using arrows and this hasn't ran yet
                if (!this._hasDownBeenPressed && !e.hasRanOnce) {
                    google.maps.event.trigger(e.target, 'keydown', {
                        keyCode: 40,
                        hasRanOnce: true,
                        stopPropagation: () => {},
                        preventDefault: () => {},
                    });
                }
            }

            // wait for google autocomplete list to be rendered before checking for selected item
            setTimeout(() => {
                // ArrowDown || ArrowUp
                if (e.keyCode === 40 || e.keyCode === 38) {
                    // if there is a selected item in the autocomplete prediction list - prevent selecting the next item
                    const hasSelected = document.querySelector('.pac-item-selected');
                    this._hasDownBeenPressed = !!hasSelected;
                }
            }, 100);
        });
    }

    onPlaceChange() {
        let place = this.searchBox.getPlace();

        if (!place || !place.geometry) {
            return;
        }

        this.place = place;
        this.searchString = place.formatted_address;
    }

    focus() {
        this.$refs.input.focus();
    }

    clear() {
        this.$refs.input.value = '';
        this.searchString = '';
        this.$refs.input.focus();
    }
}

defineElement('retailer-places-input', RetailerPlacesInput);
